import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { calculateJobKPIs } from "@/lib/jobUtils";
import { JobStatus, type AdminStats, RewardStatus } from "@/lib/schema";
import { JobHubJob } from "@/lib/jobUtils";
import { formatRewardAmount } from "@/lib/textUtils";
import { useQuery } from "@tanstack/react-query";
import { getAllRewards } from "@/api/stats";

// Import dashboard icons for consistency
import iconTotalReferrals from "@/assets/dashboard/icon_totalreferrals.svg";
import iconRewardsEarned from "@/assets/dashboard/icon_rewardsearned.svg";
import iconHired from "@/assets/dashboard/icon_hired.svg";
import iconInterviewing from "@/assets/dashboard/icon_interviewing.svg";

interface KPICardsProps {
  jobsData: JobHubJob[];
  adminStats: AdminStats | undefined;
  loading?: boolean;
  hideRewards?: boolean; // Hide rewards-related KPI cards for recruiters
}

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: string; // Changed to string for SVG file path
  variant?: "default" | "dark";
  isReward?: boolean; // Flag to identify reward-related cards
  loading?: boolean;
}

const KPICard = ({ title, value, subtitle, icon, variant = "default", isReward = false, loading }: KPICardProps) => {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="w-10 h-10 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isDark = variant === "dark";
  
  return (
    <Card 
      className={`border-0 shadow-sm ${isDark ? "text-white" : ""}`}
      style={isDark ? { backgroundColor: '#2D2D2D' } : {}}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent 
        className="p-0"
        style={{
          display: 'flex',
          padding: '16px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px'
        }}
      >
        <img 
          src={icon} 
          alt={title}
          className="h-6 w-6 sm:h-7 sm:w-7"
        />
        <div className="flex items-baseline gap-1">
          <p className={`text-2xl sm:text-3xl font-bold space-grotesk ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            {isReward ? `R${formatRewardAmount(value)}` : value}
          </p>
          {subtitle && (
            <p className={`text-lg space-grotesk ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}>
              {subtitle}
            </p>
          )}
        </div>
        <p className={`text-xs font-medium uppercase space-grotesk leading-tight tracking-[0.07px] ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}>
          {title}
        </p>
      </CardContent>
    </Card>
  );
};

export const KPICards = ({ jobsData, adminStats, loading, hideRewards = false }: KPICardsProps) => {
  // Fetch actual rewards data (only if not hiding rewards)
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery({
    queryKey: ["admin-kpi-rewards"],
    queryFn: getAllRewards,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !hideRewards, // Don't fetch rewards data if hiding rewards
  });

  // Calculate KPIs from real data
  const jobKPIs = calculateJobKPIs(jobsData);
  const totalJobs = jobsData.length;
  
  // Calculate total hires from job data (sum of currentHires across all jobs)
  const totalHires = jobsData.reduce((sum, job) => sum + (job.currentHires || 0), 0);
  
  // Calculate pending rewards from actual rewards data
  const pendingRewards = rewards
    .filter(reward => reward.status === RewardStatus.PENDING)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);

  const kpiData = [
    {
      title: "Active Jobs",
      value: jobKPIs.activeJobs.toString(),
      subtitle: `/${totalJobs}`,
      icon: iconInterviewing,
    },
    {
      title: "Rewards Pending",
      value: pendingRewards.toString(),
      icon: iconRewardsEarned,
      isReward: true
    },
    {
      title: "Placements",
      value: totalHires.toString(),
      icon: iconHired,
    },
    {
      title: "Referrals",
      value: adminStats?.totalReferrals?.toString() || "0",
      icon: iconTotalReferrals,
      variant: "dark" as const, // Apply dark style to referrals
    }
  ];

  // Filter out rewards-related cards if hideRewards is true
  const filteredKpiData = hideRewards 
    ? kpiData.filter(kpi => !kpi.isReward)
    : kpiData;

  const isLoading = loading || rewardsLoading;

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${hideRewards ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4 lg:gap-6 mb-6 lg:mb-8`}>
      {filteredKpiData.map((kpi, index) => (
        <KPICard
          key={index}
          title={kpi.title}
          value={kpi.value}
          subtitle={kpi.subtitle}
          icon={kpi.icon}
          variant={kpi.variant}
          isReward={kpi.isReward}
          loading={isLoading}
        />
      ))}
    </div>
  );
};
